/*
 * Author: harper
 * File Created: Friday, 24-07-05 03:34 pm
 * Last Modified: Monday, 25-04-14 04:46 pm, by Harper Yan
 */
import { useEffect, useMemo } from 'react';
import type { AppProps } from 'next/app';
import getConfig from 'next/config';
import Head from 'next/head';
import { appWithTranslation } from 'next-i18next';
import 'core-js/full/promise/with-resolvers';
import type { LOCALE } from '@ucard/next-core/@types';
import { GTM, ServiceWorker } from '@ucard/next-core/components';
import { FullPublicPath } from '@ucard/next-core/configs';
import { errorCodeManager } from '@ucard/next-core/managers';
import { GlobalStyles } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { AppCacheProvider } from '@mui/material-nextjs/v14-pagesRouter';
import { MessageWrapper, showSnackbar } from '@ucard/ds/message';
import theme from '@ucard/ds/theme';
import { ApolloProvider, graphql, initApolloClient } from '@web/graphql';
import { ErrorAction, setAxiosCallBack } from '@web/http';
import { assignUrl, cookies, DEFAULT_ERROR_URL, isOpenApi, qsParse, USE_CUSTOM_ERROR_PAGE } from '@web/utils';
import { basePath, USD_CURRENCY_UNIT } from '@/constants';
import useFingerprintjs from '@/hooks/useFingerprintjs';
import useTermsConditions from '@/hooks/useTermsConditions';
import { getQuoteAsset } from '@/services/asset.service';
import { useCurrencyRatio } from '@/stores/useCurrencyRatioStore';
import { getUserAsset, useUserAsset } from '@/stores/useUserAsset';
import { useKycStatusInfo } from '@/stores/useUserStore';
import { globalStyles } from './style';
const {
  publicRuntimeConfig
} = getConfig();
setAxiosCallBack({
  onLogin: () => {
    if (typeof window !== 'undefined') {
      const openApi = qsParse(cookies.get('x-open-id') ?? {});
      cookies.clear('x-login');
      // remove open api cookie
      cookies.clear('x-open-id');

      // openAPI 用户是否跳转自定义报错页面
      if (openApi.externalUserId || isOpenApi) {
        const url = USE_CUSTOM_ERROR_PAGE.includes((openApi.channel ?? '') as string) && openApi.errorRedirectUrl ? openApi.errorRedirectUrl : DEFAULT_ERROR_URL;

        // 如果是openAPI，跳转到错误页面
        window.location.href = assignUrl(url as string, {
          code: '10001'
        });
        return ErrorAction.RETURN;
      }

      // if page is login. return
      if (!(window.location.pathname.includes('/login') || FullPublicPath.includes(window.location.pathname))) {
        window.location.href = `${window.location.origin}${basePath}/login`;
        return ErrorAction.HALT;
      }
    }
  },
  onError: async error => {
    if (typeof window !== 'undefined' && error.code === 'MFA_FAILED_FREEZE_FOR_LOGIN') {
      const openApi = qsParse(cookies.get('x-open-id') ?? {});

      // openAPI 用户 不处理
      if (!(isOpenApi || openApi.externalUserId)) {
        // 前端处理cookie 信息
        cookies.clear('p20t');
        cookies.clear('x-login');
        window.location.href = `${process.env.NEXT_PUBLIC_SITE_HOST}${basePath}/login`;
        return ErrorAction.RETURN;
      }
    }
    const detail = await errorCodeManager.getDetail(error.code, error.locale as LOCALE);
    if (typeof window !== 'undefined') {
      errorCodeManager.showErrorInfo(detail, error.data);
    }
    if (detail.action === 'none') {
      return ErrorAction.RETURN;
    }
  }
});
function MyApp(props: AppProps) {
  useFingerprintjs();
  const {
    loopKycStatusWhenPending
  } = useKycStatusInfo();
  const {
    setState
  } = useCurrencyRatio(['setState']);
  useTermsConditions();
  const {
    Component,
    pageProps
  } = props;
  const {
    init
  } = useUserAsset(['init']);
  useMemo(() => {
    initApolloClient({
      onError: () => {
        // show snackbar on client side
        showSnackbar({
          message: 'System error, please try again later',
          autoHideDuration: 3000
        });
      }
    });
    init();
    if (pageProps.currencyRatioMap) {
      setState({
        currencyRatioMap: pageProps.currencyRatioMap
      });
    } else {
      const {
        selectedCurrency
      } = getUserAsset();
      if (selectedCurrency !== USD_CURRENCY_UNIT) {
        getQuoteAsset({
          base: USD_CURRENCY_UNIT,
          quote: selectedCurrency
        }).then(res => {
          setState({
            currencyRatioMap: {
              [selectedCurrency]: res.data
            }
          });
        }, () => {
          // no-op
        });
      }
    }
  }, []);
  useEffect(() => {
    return loopKycStatusWhenPending();
  }, [loopKycStatusWhenPending]);
  const client = graphql.getApolloClient();
  return <AppCacheProvider {...props} data-sentry-element="AppCacheProvider" data-sentry-component="MyApp" data-sentry-source-file="_app.page.tsx">
      <GlobalStyles styles={globalStyles} data-sentry-element="GlobalStyles" data-sentry-source-file="_app.page.tsx" />
      <Head data-sentry-element="Head" data-sentry-source-file="_app.page.tsx">
        <meta content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" name="viewport" data-sentry-element="meta" data-sentry-source-file="_app.page.tsx" />
        <title>DeCard</title>
      </Head>
      <ThemeProvider theme={theme} data-sentry-element="ThemeProvider" data-sentry-source-file="_app.page.tsx">
        {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
        <CssBaseline data-sentry-element="CssBaseline" data-sentry-source-file="_app.page.tsx" />
        <ApolloProvider client={client} data-sentry-element="ApolloProvider" data-sentry-source-file="_app.page.tsx">
          <MessageWrapper data-sentry-element="MessageWrapper" data-sentry-source-file="_app.page.tsx">
            <Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.page.tsx" />
          </MessageWrapper>
        </ApolloProvider>

        {/* eslint-disable-next-line react/jsx-pascal-case -- ignore */}
        <GTM data-sentry-element="GTM" data-sentry-source-file="_app.page.tsx" />
        {publicRuntimeConfig.SW && <ServiceWorker />}
      </ThemeProvider>
    </AppCacheProvider>;
}
export default appWithTranslation(MyApp);